<!-- Routed create page (users/create) — styled with the Phase 3 editor
     template (this form is the User editor's twin and is far too large for a
     modal; the create-modal template applies to the small create forms). -->

<template>
  <div class="bui-entity-listing-page create-user-redesign">
    <div class="create-user-redesign__inner">
      <!-- Page header -->
      <header class="pe-header">
        <div class="pe-header__left">
          <div class="pe-header__title-row">
            <h1 class="bui-page-title">Create User</h1>
          </div>
          <div class="pe-header__subtitle">{{ displayName }}</div>
        </div>
      </header>

      <!-- Two-column editor grid -->
      <section class="editor-grid">
        <!-- LEFT COLUMN -->
        <div class="editor-col">
          <!-- Profile -->
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Profile</h2>
            </template>

            <StatusBar
              :errorMessage="errorMessage"
              :isLoading="isLoading"
              loadingMessage="Saving..."
              :isSuccess="isSuccess"
              successMessage="Saved!"
            />

            <div class="field-content">
              <!-- TODO: think about adding below-field error message & error state indication (e.g. red border) [DSP-N26CxI4P] -->
              <div class="d-flex field-row">
                <label for="cu-first-name" class="basis-1/2 text-sm font-bold">First Name &#9733;</label>
                <TextField id="cu-first-name" v-model="values.firstName" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="cu-last-name" class="basis-1/2 text-sm font-bold">Last Name &#9733;</label>
                <TextField id="cu-last-name" v-model="values.lastName" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="cu-email" class="basis-1/2 text-sm font-bold">Email Address &#9733;</label>
                <TextField id="cu-email" v-model="values.username" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="cu-phone" class="basis-1/2 text-sm font-bold">Phone</label>
                <TextField id="cu-phone" v-model="values.phone" class="basis-1/2" width="100%" />
              </div>
            </div>
          </InfoCard>

          <!-- Address -->
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Address</h2>
            </template>

            <div class="field-content">
              <div class="d-flex field-row">
                <label for="cu-address1" class="basis-1/2 text-sm font-bold">Address</label>
                <TextField id="cu-address1" v-model="values.address1" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="cu-address2" class="basis-1/2 text-sm font-bold" aria-label="Address line 2"></label>
                <TextField id="cu-address2" v-model="values.address2" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="cu-region" class="basis-1/2 text-sm font-bold">Region</label>
                <div class="basis-1/2 bui-select">
                  <select id="cu-region" v-model="values.region">
                    <option :value="region" v-for="region of regions" :key="region.name">
                      {{ region.name }}
                    </option>
                  </select>
                  <PhCaretDown :size="14" weight="bold" class="bui-select__caret" />
                </div>
              </div>
              <div class="d-flex field-row">
                <label for="cu-country" class="basis-1/2 text-sm font-bold">Country</label>
                <div class="basis-1/2 bui-select">
                  <select
                    id="cu-country"
                    v-model="values.country"
                    :disabled="values?.region?.countries?.length > 0 ? false : true"
                  >
                    <option :value="country" v-for="country of values.region.countries" :key="country.name">
                      {{ country.name }}
                    </option>
                  </select>
                  <PhCaretDown :size="14" weight="bold" class="bui-select__caret" />
                </div>
              </div>
              <div class="d-flex field-row">
                <label for="cu-state" class="basis-1/2 text-sm font-bold">State</label>
                <div class="basis-1/2 bui-select">
                  <select
                    id="cu-state"
                    v-model="values.state"
                    :disabled="values?.country?.states?.length > 0 ? false : true"
                  >
                    <option :value="state" v-for="state of values.country.states" :key="state.name">
                      {{ state.name }}
                    </option>
                  </select>
                  <PhCaretDown :size="14" weight="bold" class="bui-select__caret" />
                </div>
              </div>
              <div class="d-flex field-row">
                <label for="cu-city" class="basis-1/2 text-sm font-bold">City</label>
                <TextField id="cu-city" v-model="values.city" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="cu-zip" class="basis-1/2 text-sm font-bold">Postal Code</label>
                <TextField id="cu-zip" v-model="values.zip" class="basis-1/2" width="100%" />
              </div>
            </div>
          </InfoCard>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="editor-col">
          <!-- Organization & Access -->
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Organization &amp; Access</h2>
            </template>

            <div class="field-content">
              <div class="d-flex field-row">
                <label class="basis-1/2 text-sm font-bold">Organization &#9733;</label>
                <div class="basis-1/2 cu-inline-dialog">
                  <SelectOrganization
                    :btnTitle="values.group?.name ? values.group?.name : 'Select Group'"
                    :showBtn="false"
                    @save="addOrgainzation($event)"
                  />
                </div>
              </div>
              <div class="d-flex field-row field-row--top">
                <label for="cu-roles" class="basis-1/2 text-sm font-bold">Roles</label>
                <select
                  id="cu-roles"
                  class="basis-1/2 cu-roles"
                  v-model="values.roles"
                  multiple="true"
                >
                  <option :value="role" v-for="role of roles" :key="role.id">
                    {{ role.name }}
                  </option>
                </select>
              </div>
              <div class="d-flex field-row field-row--top">
                <label class="basis-1/2 text-sm font-bold">Flags</label>
                <div class="basis-1/2 cu-flags">
                  <label class="cu-flag">
                    <input type="checkbox" v-model="values.auditorFlag" />
                    Asset Auditor
                  </label>
                  <label class="cu-flag">
                    <input type="checkbox" v-model="values.accountFlag" />
                    Account Admin
                  </label>
                  <label class="cu-flag">
                    <input type="checkbox" v-model="values.superUserFlag" />
                    Super User
                  </label>
                  <label class="cu-flag">
                    <input type="checkbox" v-model="values.analyticsFlag" />
                    Analytics
                  </label>
                </div>
              </div>
            </div>
          </InfoCard>
        </div>
      </section>

      <!-- Sticky footer -->
      <footer class="editor-footer">
        <div class="editor-footer__inner">
          <ButtonCustomized label="Exit" v-if="!hasChanges" @click="onExit" ctaSecondary />
          <BeforeExitDialogue v-if="hasChanges" @save="submitAndExit" @exit="onExit" />
          <ButtonCustomized label="Save" cta @click="onSubmit" :disabled="!hasChanges" />
          <Saving ref="savingDialog" />
        </div>
      </footer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { PhCaretDown } from '@phosphor-icons/vue';
import TextField from "@/components/form-fields/TextField.vue";
import useCreateUserForm from "./CreateUserHelper";
import ButtonCustomized from "@/components/form-fields/ButtonCustomized.vue";
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";
import useNotifications from "@/components/Notifications/composables/notifications";
import router from '@/router';
import SelectOrganization from "./SelectOrganizationDialog.vue";
import { getRegion } from "@/helpers/countries/countries";
import BeforeExitDialogue from '@/components/Dialog/BeforeExitDialog/BeforeExitDialogue.vue';
import Saving from '@/components/Dialog/SavingDialog/Saving.vue'
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';

const notification = useNotifications();
const savingDialog = ref()
const regions = getRegion();
const { values, submitForm, isLoading, isSuccess, errorMessage,
  newUserId, getRolesList, selectedGroup, roles, hasChanges, removeBeforeLoad } = useCreateUserForm();

onMounted(() => {
  getRolesList();
});

const displayName = computed(() => {
  return [values.firstName, values.lastName].filter(Boolean).join(' ');
});

async function onSubmit() {
  await submitForm();
  if (isSuccess.value === true) {
    notification.success('User created successfully!');
    router.push({ name: 'user-editor', params: { id: newUserId.value } });
  }
}
function addOrgainzation(group: any) {
  values.group = group
}
async function submitAndExit() {
  savingDialog.value.open()
  await submitForm();
  savingDialog.value.close()
  if (isSuccess.value === true) {
    notification.success('user created successfully!');
    removeBeforeLoad();
    router.push({ name: 'user-editor', params: { id: newUserId.value } });
  }
}

function onExit() {
  removeBeforeLoad()
  router.push({ name: 'users' });
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

/* Outer wrapper: paints the BUI page surface edge-to-edge (see the note in
   PlayerEditorView — `.bui-entity-listing-page` is wired in PageLayout.vue). */
.create-user-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.create-user-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

/* Roles list (multi-select). */
.cu-roles {
  min-height: 96px;
  margin: 0;
  padding: 0.25rem;
  font-size: 14px;
  font-family: inherit;
  color: var(--bui-ter-input-text, #1a1a1e);
  background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
  border: 1px solid var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
  border-radius: var(--bui-ter-radius, 8px);
  outline: none;

  option {
    padding: 0.25rem 0.35rem;
    border-radius: 4px;
  }

  &:focus-visible {
    outline: 2px solid var(--bui-chk-checked-fill, #de3da9);
    outline-offset: 1px;
  }
}

/* ---- Flags checkbox list ---- */
.cu-flags {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.cu-flag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--bui-tbl-row-text, #2a2a30);
  cursor: pointer;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: var(--bui-chk-checked-fill, #de3da9);
    cursor: pointer;
  }
}

/* Inline dialog expanders (Select Organization) hug content and align left. */
.cu-inline-dialog {
  :deep(> .flex),
  :deep(.d-inline) {
    display: inline-flex;
    width: auto;
    flex: 0 0 auto;
    justify-content: flex-start;
  }
}
</style>
